/**
 * Applies the Usage statistics choice to Google Analytics for the app.
 * privacyTracking calls it when choices or pages change; this module handles
 * Google's script, reviewed page views, and shutdown.
 */
import { getReviewedTrackingPage } from "@/utils/trackingLocation"

type AnalyticsWindow = typeof window & {
	dataLayer?: unknown[]
	gtag?: (...args: unknown[]) => void
	[key: `ga-disable-${string}`]: boolean
}

enum AnalyticsState {
	Idle,
	Loading,
	Loaded,
	Active,
	Stopped,
	Failed,
}

const measurementId = import.meta.env.VITE_GOOGLE_ANALYTICS_MEASUREMENT_ID ?? "G-8R3YT3CE62"
const scriptId = "landerz-google-analytics"
const cookiePrefix = "landerz_ga4"
const browser = window as AnalyticsWindow

let state = AnalyticsState.Idle
let currentPage: string | undefined
let lastSentPage: string | undefined
let script: HTMLScriptElement | undefined

function getSafePage(): string | undefined {
	return getReviewedTrackingPage()?.href
}

function setDisabled(disabled: boolean): void {
	browser[`ga-disable-${measurementId}`] = disabled
}

function pageParameters() {
	return {
		page_location: currentPage,
		page_title: "Landerz",
		page_referrer: "",
	}
}

function sendCurrentPage(): void {
	if (!currentPage || (state !== AnalyticsState.Loaded && state !== AnalyticsState.Active)) return
	// Refresh these overrides before any event, so the SDK never needs the raw URL.
	browser.gtag?.("set", pageParameters())
	setDisabled(false)
	if (state === AnalyticsState.Loaded) {
		browser.gtag?.("consent", "update", {
			analytics_storage: "granted",
			ad_storage: "denied",
			ad_user_data: "denied",
			ad_personalization: "denied",
		})
		browser.gtag?.("js", new Date())
		browser.gtag?.("config", measurementId, {
			...pageParameters(),
			send_page_view: false,
			allow_google_signals: false,
			allow_ad_personalization_signals: false,
			ignore_referrer: true,
			cookie_domain: "none",
			cookie_prefix: cookiePrefix,
			cookie_path: "/",
			// Match the proposed 180-day choice lifetime; do not extend on each visit.
			cookie_expires: 180 * 24 * 60 * 60,
			cookie_update: false,
			cookie_flags: "SameSite=Lax;Secure",
		})
		state = AnalyticsState.Active
	}
	if (currentPage === lastSentPage) return
	browser.gtag?.("event", "page_view", { ...pageParameters(), send_to: measurementId })
	lastSentPage = currentPage
}

export function applyGoogleAnalyticsChoice(allowed: boolean): boolean {
	if (!allowed) {
		if (state !== AnalyticsState.Idle) stopGoogleAnalytics()
		return false
	}
	if (state === AnalyticsState.Stopped || state === AnalyticsState.Failed) return false
	// Enabled for production unless explicitly switched off. The PR review must
	// verify stream settings; send_page_view alone cannot stop enhanced measurement.
	if (import.meta.env.VITE_ENABLE_GOOGLE_ANALYTICS === "false") return false
	if (window.location.hostname !== "app.landerz.ca" || !/^G-[A-Z0-9]+$/.test(measurementId)) return false
	currentPage = getSafePage()
	if (!currentPage) {
		if (state !== AnalyticsState.Idle) setDisabled(true)
		lastSentPage = undefined
		return false
	}
	if (state !== AnalyticsState.Idle) {
		sendCurrentPage()
		return true
	}

	state = AnalyticsState.Loading
	setDisabled(true)
	browser.dataLayer = []
	browser.gtag = function (..._args: unknown[]) {
		browser.dataLayer?.push(arguments)
	}
	browser.gtag("consent", "default", {
		analytics_storage: "denied",
		ad_storage: "denied",
		ad_user_data: "denied",
		ad_personalization: "denied",
	})
	browser.gtag("set", pageParameters())
	script = document.createElement("script")
	script.id = scriptId
	script.async = true
	script.referrerPolicy = "no-referrer"
	// This is Google's direct GA4 SDK, not a GTM container.
	script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`
	script.onload = () => {
		if (state !== AnalyticsState.Loading) return
		currentPage = getSafePage()
		state = AnalyticsState.Loaded
		sendCurrentPage()
	}
	script.onerror = () => {
		if (state !== AnalyticsState.Loading) return
		stopGoogleAnalytics()
		state = AnalyticsState.Failed
	}
	document.head.appendChild(script)
	return true
}

export function stopGoogleAnalytics(): void {
	// Removing a script cannot cancel prepared execution. Disable the SDK first,
	// discard unsent commands, and never restart this runtime before refresh.
	setDisabled(true)
	state = AnalyticsState.Stopped
	currentPage = undefined
	if (browser.dataLayer) browser.dataLayer.length = 0
	if (script) {
		script.onload = null
		script.onerror = null
		script.remove()
	}
	// These cookies are host-only. Leave auth and the public website's cookies alone.
	for (const cookie of document.cookie.split(";")) {
		const name = cookie.trim().split("=", 1)[0]
		if (name.startsWith(`${cookiePrefix}_`)) {
			document.cookie = `${name}=; Max-Age=0; Path=/; SameSite=Lax; Secure`
		}
	}
}
